import { AbstractDialogContent } from '@abstracts/dialog/dialog-content';
import { Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PricingsService } from '@services/entity/pricings.service';
import { ToastService } from '@services/toast/toast.service';
import { UsersService } from '@services/entity/users.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { FormService } from '@services/form/form.service';
import { InvoicesService } from '@services/entity/invoices.service';
import { TransactionsService } from '@services/entity/transactions.service';
import { UserDto } from '@dto/userDto';
import { TransactionDto } from '@dto/transactionDto';
import { InvoiceDto } from '@dto/invoiceDto';
import { PricingDto } from '@dto/pricingDto';
import { InvoiceItemDto } from '@dto/invoiceItemDto';
import { plainToClass } from 'class-transformer';
import { Invoice } from '@models/invoice';
import { Transaction } from '@models/transaction';
import { InvoiceSettingsDto } from '@dto/invoiceSettingsDto';
import { CustomersService } from '@services/entity/customers.service';
import { ActivatedRoute } from '@angular/router';
import { BillingMethodEnum } from '@enums/billingMethodEnum.enum';

@Component({
  selector: 'app-create-invoice-dialog-content',
  templateUrl: './create-invoice-dialog-content.component.html',
  styleUrls: ['./create-invoice-dialog-content.component.scss']
})
export class CreateInvoiceDialogContentComponent extends AbstractDialogContent implements OnInit {

  public className = 'CreateInvoiceDialogContentComponent';

  @Input() private userId: string;
  @Input() private customerId: number;

  public user: UserDto
  public recipient: UserDto
  public users?: UserDto[] = []
  public availableUsers?: UserDto[] = []
  public usersSelectEmpty: string = 'Keine Benutzer vorhanden'

  public invoiceSettings: InvoiceSettingsDto
  public invoices: InvoiceDto[] = []
  public invoiceItems: InvoiceItemDto[] = []
  public selectedInvoiceItems: InvoiceItemDto[] = []
  public pricings: PricingDto[] = []

  public isFormReady: boolean = false
  public isTableReady: boolean = false

  public isUser: boolean = false;
  public isCustomer: boolean = false;
  public isOperator: boolean = false;
  public isSupporter: boolean = false;

  @Output() newEntityEvent = new EventEmitter<Invoice>();

  constructor(
    public activeModal: NgbActiveModal,
    public pricingService: PricingsService,
    public spinnerService: NgxSpinnerService,
    public activatedRoute: ActivatedRoute,
    public usersService: UsersService,
    public customersService: CustomersService,
    public transactionsService: TransactionsService,
    public formService: FormService,
    public invoicesService: InvoicesService,
    public toastService: ToastService,
    injector: Injector) {
    super(activeModal, injector);
  }

  public ngOnInit(): void {
    this.getRoles()
    this.getUser()
    this.getRecipient(this.userId)
  }

  getRoles() {
    if (this.authenticationService.userHasRole('Operator')) this.isOperator = true;
    if (this.authenticationService.userHasRole('Customer')) this.isCustomer = true;
    if (this.authenticationService.userHasRole('User')) this.isUser = true;
    this.isSupporter = this.authenticationService.userHasRole('Supporter')
  }

  getRecipient(userId: string){
    this.usersService.getUserDtoById(userId).subscribe(recipient => {
      this.recipient = plainToClass(UserDto, recipient)
      this.initializeForm()
    })
  }

  getUser() {
    if (this.isOperator) {
      this.customersService.getMasterUserOfCustomer(this.customerId).subscribe(masterUser => {
        this.usersService.getUserDtoById(masterUser.id).subscribe(user => {
          this.user = user;
          this.getInvoiceSettings()
          this.getInvoices();
        })
      })
    } else {
      this.usersService.getLoggedInUser().subscribe(user => {
        this.user = plainToClass(UserDto, user)
        this.getInvoiceSettings()

        if (this.isCustomer) {
          this.getInvoices();
        }
      })
    }
  }

  getInvoiceSettings() {
    this.invoicesService.getInvoiceSettingsOfUser(this.user.id).subscribe(invoiceSettings => {
      this.invoiceSettings = plainToClass(InvoiceSettingsDto, invoiceSettings)
    }, error => {})
  }

  getInvoices() {
    if (!this.user) return false
    this.invoicesService.getInvoicesOfCustomer(this.user.id).subscribe(invoices => {
      this.invoices = plainToClass(InvoiceDto, invoices)
      this.getInvoiceItems()
    })
  }

  getInvoiceItems() {
    if (this.user.selectedCustomerId == undefined) return false
    this.invoicesService.getAllUnbilledInvoiceItemsOfRecipientForManualBillingAndAutoBilling(this.recipient.id).subscribe(invoiceItems => {
      const invoiceItem = plainToClass(InvoiceItemDto, invoiceItems)
      this.invoiceItems = invoiceItem
      this.selectedInvoiceItems = invoiceItem.filter(ii => ii.billingMethod == BillingMethodEnum.Manual)
      this.isTableReady = true
    })
  }

  toggleInvoiceItem(invoiceItemId) {
    var item = this.invoiceItems.find(ii => ii.id == invoiceItemId)
    if (item.billingMethod != BillingMethodEnum.Manual) return
    const index = this.selectedInvoiceItems.findIndex(ii => ii.id == invoiceItemId)
    if (index == -1) {
      const item = this.invoiceItems.find(ii => ii.id == invoiceItemId)
      this.selectedInvoiceItems.push(item)
    } else {
      this.selectedInvoiceItems.splice(index, 1)
    }
  }

  isChecked(invoiceItemId) {
    var item = this.selectedInvoiceItems.find(t => t.id == invoiceItemId)
    if (item == null) return false
    return item?.billingMethod == BillingMethodEnum.Manual
  }

  getInvoiceMeterAmount() {
    if (this.selectedInvoiceItems.length < 1) return 0
    const invoiceMeterAmount = this.selectedInvoiceItems
      .map(ii => ii.quantity)
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0)
    return invoiceMeterAmount
  }

  getInvoiceAmount() {
    if (this.selectedInvoiceItems.length < 1) return 0
    const invoiceAmount = this.selectedInvoiceItems
      .map(ii => ii.total)
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0)
    return invoiceAmount
  }

  private initializeForm() {
    this.formService.validators = {
      company: [],
      firstName: [Validators.required, Validators.maxLength(50)],
      lastName: [Validators.required, Validators.maxLength(50)],
      gender: [],
      street: [Validators.required],
      houseNumber: [Validators.required],
      postcode: [Validators.required, Validators.pattern('^[0-9]*$'), Validators.maxLength(5), Validators.minLength(5)],
      city: [Validators.required],
      email: [],
    }

    this.formService.form = new UntypedFormGroup({
      company: new UntypedFormControl(this.recipient.company, this.formService.validators['company']),
      firstName: new UntypedFormControl(this.recipient.firstName, this.formService.validators['firstName']),
      lastName: new UntypedFormControl(this.recipient.lastName, this.formService.validators['lastName']),
      gender: new UntypedFormControl(this.recipient.gender, this.formService.validators['gender']),
      street: new UntypedFormControl(this.recipient.street, this.formService.validators['street']),
      houseNumber: new UntypedFormControl(this.recipient.houseNumber, this.formService.validators['houseNumber']),
      postcode: new UntypedFormControl(this.recipient.postcode, this.formService.validators['postcode']),
      city: new UntypedFormControl(this.recipient.city, this.formService.validators['city']),
      email: new UntypedFormControl(this.recipient.email, this.formService.validators['email']),
    });

    this.formService.form.markAllAsTouched()
    if (this.isSupporter) this.formService.form.disable()
    this.isFormReady = true
  }

  public submit(download: boolean = false) {
    if (this.selectedInvoiceItems.length < 1) return false
    if (this.selectedInvoiceItems.some(item => item.billingMethod !== BillingMethodEnum.Manual)) return false

    const form = this.formService.form.value
    const invoice = new InvoiceDto()

    invoice.ownerUserId = this.user.id

    invoice.recipientUserId = this.userId
    invoice.recipientCity = form.city
    invoice.recipientCompany = form.company
    invoice.recipientFirstName = form.firstName
    invoice.recipientLastName = form.lastName
    invoice.recipientGender = form.gender
    invoice.recipientStreet = form.street
    invoice.recipientHouseNumber = form.houseNumber
    invoice.recipientPostcode = form.postcode
    invoice.recipientEmail = form.email

    invoice.invoiceItems = this.selectedInvoiceItems as InvoiceItemDto[]
    this.invoicesService.createInvoice(invoice).subscribe(newInvoice => {
      this.toastService.success(`Rechnung erfolgreich erstellt.`)
      if (download) {
        this.toastService.loading(`Rechnung wird heruntergeladen...`)
        this.invoicesService.getInvoicePdf(newInvoice.id).subscribe((response: any) => {
          const file = new Blob([response], { type: 'application/pdf' });
          const fileUrl = URL.createObjectURL(file);

          var a = document.createElement('a');
          document.body.appendChild(a);
          a.style.display = 'none';
          a.href = fileUrl;
          a.download = this.getFilename(invoice) + '.pdf';
          a.click();

          window.URL.revokeObjectURL(fileUrl);
          this.activeModal.close();
        }, error => {
          console.error(error);
          this.toastService.error(`Rechnung konnte nicht heruntergeladen werden.`, 0, true)
        })
        // this.router.navigate([`invoices/print/${newInvoice.id}`])
      } else {
        this.activeModal.close()
      }
    }, error => {
      this.toastService.error(`Fehler beim Erstellen der Rechnung.`)
      this.messageService.setError(error)
    })
  }

  getFilename(invoice: InvoiceDto) {
    let filenameFirstVariable = 'invoiceNumber';
    if (localStorage.getItem('invoiceFilenameFirstVariable') != undefined) {
      filenameFirstVariable = localStorage.getItem('invoiceFilenameFirstVariable')
    }

    let filenameSecondVariable;
    if (localStorage.getItem('invoiceFilenameSecondVariable') != undefined) {
      filenameSecondVariable = localStorage.getItem('invoiceFilenameSecondVariable')
    }

    let filenameThirdVariable;
    if (localStorage.getItem('invoiceFilenameThirdVariable') != undefined) {
      filenameThirdVariable = localStorage.getItem('invoiceFilenameThirdVariable')
    }

    let name = invoice[filenameFirstVariable]
    let filename = this.isDate(name) ? this.parseDate(name) : name.replace(/ /g, '_')

    if (filenameSecondVariable) {
      let name = invoice[filenameSecondVariable]
      if (this.isDate(name)) name = this.parseDate(name)
      filename += `_${name.replace(/ /g, '_')}`
    }

    if (filenameThirdVariable) {
      let name = invoice[filenameThirdVariable]
      if (this.isDate(name)) name = this.parseDate(name)
      filename += `_${name.replace(/ /g, '_')}`
    }

    return filename += '.pdf';
  }

  public getDueDate(date, days) {
    var result = new Date(date)
    result.setDate(result.getDate() + days)
    return result
  }

  isDate(value: string) {
    if (!value || typeof value != 'string') return false
    return new RegExp(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z*/).test(value.trim())
  }

  parseDate(date: Date) {
    if (!date) return false
    return new Date(date).toLocaleDateString()
  }

  getMonth(date: Date) {
    if (!date) return false
    const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
    return months[new Date(date).getMonth()]
  }

  close() {
    this.activeModal.close();
  }
}
